import { useIsHardwareConnected } from '../../context/hardware';
import { useLocalization } from '../../context/Localization/localization';
import { getFullDisplayName } from '../../helpers/Appointment';
import { formatAsPhoneNumber } from '../../helpers/Number';
import { useDisplayLocalizedVehicle, DisplayMake } from '../../helpers/displayVehicle';
import css from './AppointmentCard.module.scss';
import { GMGenericIcon } from '../Icon';
import { GMGenericButton } from '../ButtonBase';
import { GoMotoComponent, GoMotoFunction } from '../../API/GoMoto.types';
import { AppointmentCardProps } from './AppointmentCard.types';
import { useFormatAppointmentDateTime } from '../../context/Appointment/appointment';
import { AppointmentTimeFormatTypes, KeyExchangeStage } from '../../context/Appointment/appointment.types';
import { GMGrid } from '../Grid';
import classNames from 'classnames';

/**
 * This component is used to display the tiles in the Key Management screen as well as the results
 * from the name filter in the Key Management screen. This is a quick visual tile that gives "at-a-glance"
 * information about an appointment and the state it is in.
 *
 * @param appointment - {@link AppointmentCardProps.appointment appointment} description.
 * @param onAddKeyClick - {@link AppointmentCardProps.onAddKeyClick onAddKeyClick} description.
 * @param color - {@link AppointmentCardProps.color color} description.
 * @param description - {@link AppointmentCardProps.description description} description.
 */
export const AppointmentCard: GoMotoComponent<AppointmentCardProps> = ({
    // TODO: We now have an appointment provider wrapping around the admin screen, so we should use that for these cards instead.
    appointment,
    onAddKeyClick,
    color,
    description,
}) => {
    const {
        formatString,
        NoAvailable,
        VIN,
        KeyIsInBin,
        AddKey,
        ClickToAddKey,
    } = useLocalization();
    const isConnected: GoMotoFunction<[void], boolean | undefined> = useIsHardwareConnected();
    const displayLocalizedVehicle = useDisplayLocalizedVehicle();
    const { formatDateTimeWithTimeFormatType } = useFormatAppointmentDateTime();

    const isAdminKeyRetrieveStage: boolean = appointment.keyExchangeStage === KeyExchangeStage.AdminKeyRetrieve && appointment.loanerOrRentalReturnTimestamp != null;
    const isAdvisorStoredKey: boolean = appointment.storedByAdvisor === true && appointment.advisorKeyStoredTimestamp != null;

    const effectiveEmail: string = appointment.pickupEmail ? appointment.pickupEmail : appointment.email;
    const email: string | null = effectiveEmail ? effectiveEmail : null;
    const effectivePhone: string = appointment.pickupPhone ? appointment.pickupPhone : appointment.phone;
    const phone: string | null = effectivePhone ? formatAsPhoneNumber(effectivePhone) : null;
    const vehicle: string = DisplayMake(appointment) ? displayLocalizedVehicle(appointment) : formatString(NoAvailable, VIN) as string;
    const apptDate: string = formatDateTimeWithTimeFormatType({
        appointmentDateTime: isAdminKeyRetrieveStage ? appointment.loanerOrRentalReturnTimestamp
            : isAdvisorStoredKey ? appointment.advisorKeyStoredTimestamp
                : appointment.appointmentDateTime,
        timeFormatType: AppointmentTimeFormatTypes.AppointmentCard,
    });

    return (
        <GMGrid
            container
            xs={12}
            className={css.appointmentCard}
        >
            <GMGrid
                container
                xs={12}
                className={css.appointmentInfo}
                sx={{ backgroundColor: color }}
            >
                <GMGrid xs={12} className={classNames(css.name, css.ellipsesText)}>
                    {getFullDisplayName(appointment)}
                </GMGrid>
                <GMGrid xs={12} className={css.ellipsesText}>
                    {email ? email : phone ?? <>&nbsp;</>}
                </GMGrid>
                <GMGrid xs={12} className={css.ellipsesText}>
                    {phone && email ? phone : <>&nbsp;</>}
                </GMGrid>
                {appointment.keySlotNumber ? (
                    <GMGrid
                        container
                        xs={7.5}
                        className={css.keysInBin}
                        wrap={'nowrap'}
                    >
                        <GMGrid xs={8} className={css.keysInBinText}>
                            {KeyIsInBin}
                        </GMGrid>
                        <GMGrid xs={4} className={css.keysInBinCount}>
                            {appointment.keySlotNumber}
                        </GMGrid>
                    </GMGrid>
                ) : (isConnected() && onAddKeyClick ? (
                    <GMGrid
                        container
                        xs={12}
                        className={css.addKeyWrapper}
                    >
                        <GMGenericButton
                            onClick={onAddKeyClick}
                            type={'button'}
                            id={ClickToAddKey}
                            ariaLabel={ClickToAddKey}
                        >
                            <GMGrid
                                container
                                xs={7.5}
                                className={css.addKey}
                                wrap={'nowrap'}
                            >
                                <GMGrid xs={2.5} className={css.addKeyIcon}>
                                    <GMGenericIcon iconType={'Add'} />
                                </GMGrid>
                                <GMGrid xs={7}>
                                    {AddKey}
                                </GMGrid>
                            </GMGrid>
                        </GMGenericButton>
                    </GMGrid>
                ) : null)}
            </GMGrid>
            <GMGrid
                container
                xs={12}
                className={css.vehicleInfo}
                wrap={'nowrap'}
            >
                <GMGrid xs={3} className={css.vehicleIcon}>
                    <GMGenericIcon iconType={appointment.keyExchangeStage === KeyExchangeStage.AdminKeyRetrieve ? 'Key' : 'Vehicle'} />
                </GMGrid>
                <GMGrid container xs={9}>
                    <GMGrid xs={12} className={classNames(css.description, css.ellipsesText)}>
                        {description ? description : vehicle}
                    </GMGrid>
                    <GMGrid xs={12} className={classNames(css.datetime, css.ellipsesText)}>
                        {apptDate}
                    </GMGrid>
                </GMGrid>
            </GMGrid>
        </GMGrid>
    );
};